import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { calculatePoleLength, mapLengthToSizeRange, extractPoleTypeForCalculation, validateCalculationInputs, checkForRollingSpringheadRecommendation } from '../utils/poleCalculationUtils';
import { supabase } from '../utils/supabase';

// Styled Components
const CalculatorContainer = styled(motion.div)`
  background: ${props => props.embedded ? 'transparent' : 'white'};
  border: ${props => props.embedded ? 'none' : '1px solid var(--gray-200)'};
  border-radius: ${props => props.embedded ? '0' : '8px'};
  padding: ${props => props.embedded ? '0' : '1.25rem'};
  margin-top: ${props => props.embedded ? '0' : '1rem'};
  box-shadow: ${props => props.embedded ? 'none' : 'var(--shadow-sm)'};
  position: relative;
`;

// Progress Indicator Components
const StepProgress = styled.div`
  display: flex;
  gap: 0.25rem;
  margin-bottom: 1.5rem;
`;

const ProgressDot = styled.div`
  flex: 1;
  height: 3px;
  background: ${props => props.active
    ? 'linear-gradient(90deg, var(--primary-color) 0%, #e67e00 100%)'
    : 'rgba(0, 0, 0, 0.08)'};
  border-radius: 2px;
  transition: all 0.3s ease;
`;

const StepIndicatorText = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--gray-500);
  margin-bottom: 0.5rem;
`;

const StepTitleModern = styled.h4`
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--gray-900);
  margin: 0 0 1.5rem 0;
  line-height: 1.3;
`;

const CalculatorHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid var(--gray-200);
`;

const CalculatorTitle = styled.h4`
  font-size: var(--text-md);
  font-weight: 600;
  color: var(--gray-800);
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: var(--gray-400);
  font-size: 1.25rem;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  transition: all 0.2s ease;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: var(--gray-600);
    background: var(--gray-100);
  }
`;

const StepContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const StepHeader = styled.div`
  margin-bottom: 0.75rem;
`;

const StepTitle = styled.h5`
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--gray-700);
  margin: 0 0 0.5rem 0;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const InputGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-bottom: 1.5rem;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  display: block;
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--gray-700);
  margin-bottom: 0.5rem;
  letter-spacing: 0.01em;
`;

const Input = styled.input`
  width: 100%;
  height: 48px;
  padding: 0 1rem;
  border: 1px solid ${props => props.error ? 'var(--error-color)' : 'rgba(0, 0, 0, 0.08)'};
  border-radius: 8px;
  font-size: 0.9375rem;
  background: white;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${props => props.error ? 'var(--error-color)' : 'rgba(0, 0, 0, 0.12)'};
  }

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(255, 143, 0, 0.08);
    background: white;
  }
`;

const LoopTypeContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem;
  margin-bottom: 1rem;

  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`;

const LoopTypeCard = styled.div`
  padding: 1rem;
  border: 2px solid ${props => props.selected ? 'var(--primary-color)' : 'rgba(0, 0, 0, 0.06)'};
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => props.selected ? 'rgba(255, 143, 0, 0.04)' : 'white'};
  text-align: center;

  &:hover {
    border-color: var(--primary-color);
    background: rgba(255, 143, 0, 0.04);
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  }
`;

const LoopTypeLabel = styled.div`
  font-size: var(--text-sm);
  font-weight: 500;
  color: var(--gray-800);
  margin-bottom: 0.25rem;
`;

const LoopTypeDesc = styled.div`
  font-size: var(--text-xs);
  color: var(--gray-600);
`;

const SliderContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
`;

const SliderLabel = styled.span`
  font-size: var(--text-sm);
  color: var(--gray-600);
  min-width: 40px;
`;

const Slider = styled.input`
  flex: 1;
  height: 6px;
  background: var(--gray-300);
  border-radius: 3px;
  appearance: none;
  cursor: pointer;

  &::-webkit-slider-thumb {
    appearance: none;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: var(--primary-color);
    cursor: pointer;
  }

  &::-moz-range-thumb {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: var(--primary-color);
    border: none;
    cursor: pointer;
  }
`;

const SliderValue = styled.span`
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--primary-color);
  min-width: 50px;
  text-align: right;
`;

const ErrorMessage = styled.div`
  background: var(--error-light);
  border: 1px solid var(--error-color);
  color: var(--error-text);
  padding: 0.75rem;
  border-radius: 6px;
  font-size: var(--text-sm);
  margin-top: 0.5rem;
`;

const SuccessMessage = styled.div`
  background: var(--success-light);
  border: 1px solid var(--success-color);
  color: var(--success-text);
  padding: 0.75rem;
  border-radius: 6px;
  font-size: var(--text-sm);
  margin-top: 0.5rem;
`;

const CalculateButton = styled.button`
  padding: 0.5rem 1rem;
  background: ${props => props.secondary ? 'transparent' : 'var(--primary-color)'};
  color: ${props => props.secondary ? 'var(--gray-600)' : 'white'};
  border: 1px solid ${props => props.secondary ? 'var(--gray-300)' : 'var(--primary-color)'};
  border-radius: var(--rounded-md);
  font-size: var(--text-sm);
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: ${props => props.secondary ? 'var(--gray-50)' : 'var(--primary-dark)'};
    border-color: ${props => props.secondary ? 'var(--gray-400)' : 'var(--primary-dark)'};
  }

  &:disabled {
    background: var(--gray-200);
    color: var(--gray-400);
    border-color: var(--gray-200);
    cursor: not-allowed;
  }
`;

const ResultContainer = styled.div`
  background: linear-gradient(135deg, rgba(34, 197, 94, 0.05) 0%, rgba(34, 197, 94, 0.08) 100%);
  border: 2px solid rgba(34, 197, 94, 0.2);
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1rem;
`;

const ResultTitle = styled.div`
  font-size: 0.8125rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--gray-600);
  margin-bottom: 0.5rem;
`;

const ResultValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: var(--success-color);
  margin: 0.5rem 0;
  letter-spacing: -0.02em;
  line-height: 1.2;
`;

const RecommendedSizeBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: var(--success-color);
  color: white;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  margin-top: 0.75rem;

  &::before {
    content: '✓';
    font-size: 1.125rem;
  }
`;

const ResultDetails = styled.ul`
  font-size: var(--text-xs);
  color: var(--gray-500);
  margin: 0;
  padding-left: 1rem;
  
  li {
    margin-bottom: 0.125rem;
  }
`;

const UseResultButton = styled(CalculateButton)`
  width: 100%;
  margin-top: 0.5rem;
  background: var(--success-color);
  border-color: var(--success-color);
  
  &:hover:not(:disabled) {
    background: var(--success-dark);
    border-color: var(--success-dark);
  }
`;

const PoleTypeDisplay = styled.div`
  background: var(--gray-50);
  border: 1px solid var(--gray-200);
  border-radius: var(--rounded-sm);
  padding: 0.5rem 0.75rem;
  margin-bottom: 0.75rem;
  font-size: var(--text-sm);
  color: var(--gray-700);
  
  strong {
    color: var(--primary-color);
  }
`;

const RollingSpringheadWarning = styled(motion.div)`
  background: linear-gradient(to bottom, rgba(255, 248, 240, 0.95), rgba(255, 243, 230, 0.95));
  border: 1px solid rgba(255, 143, 0, 0.3);
  border-radius: 12px;
  padding: 1rem;
  margin-bottom: 1rem;
  box-shadow: 0 4px 12px rgba(255, 143, 0, 0.1);
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const WarningHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  gap: 0.5rem;
`;

const WarningIcon = styled.div`
  color: var(--primary-color);
`;

const WarningTitle = styled.div`
  font-weight: 600;
  color: var(--secondary-color);
  font-size: var(--text-sm);
  text-align: center;
`;


const WarningText = styled.div`
  font-size: var(--text-xs);
  color: var(--gray-700);
  text-align: center;
  line-height: 1.5;
  
  a {
    color: var(--primary-color);
    text-decoration: underline;
    font-weight: 500;
    
    &:hover {
      color: var(--primary-hover);
    }
  }
`;

const WarningDivider = styled.div`
  width: 100%;
  height: 1px;
  background: linear-gradient(to right, rgba(255, 143, 0, 0.1), rgba(255, 143, 0, 0.2), rgba(255, 143, 0, 0.1));
  margin: 0.25rem 0;
`;

// Warning icon component
const AlertCircle = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
    <line x1="12" y1="8" x2="12" y2="12" stroke="currentColor" strokeWidth="2"/>
    <line x1="12" y1="16" x2="12.01" y2="16" stroke="currentColor" strokeWidth="2"/>
  </svg>
);

const InlinePoleCalculator = ({
  preselectedPoleType,
  onCalculationComplete,
  onClose,
  embedded = false
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    bagLength: 100,
    bagWidth: 100,
    bagHeight: 110,
    loopType: 'Corner loop',
    loopLength: 30
  });
  const [result, setResult] = useState(null);
  const [errors, setErrors] = useState([]);
  const [rollingSpringheadRecommendation, setRollingSpringheadRecommendation] = useState(null);

  const poleType = extractPoleTypeForCalculation(preselectedPoleType);

  const getStepTitle = (step) => {
    switch(step) {
      case 1: return 'Bag Dimensions';
      case 2: return 'Loop Type';
      case 3: return 'Loop Length';
      case 4: return 'Result';
      default: return '';
    }
  };
  
  const loopTypes = [
    { id: 'corner', name: 'Corner loop', description: 'Standard corner loop option', image: 'https://qpalpdncuownwqknszro.supabase.co/storage/v1/object/public/landing-page/Cornerloop.webp' },
    { id: 'cross', name: 'Cross corner loop', description: 'Enhanced stability for heavier loads', image: 'https://qpalpdncuownwqknszro.supabase.co/storage/v1/object/public/landing-page/Crosscorner.webp' }
  ];

  // Check for Rolling Springhead recommendation when bag dimensions change
  useEffect(() => {
    if (formData.bagLength && formData.bagWidth) {
      const recommendation = checkForRollingSpringheadRecommendation({
        bagLength: formData.bagLength,
        bagWidth: formData.bagWidth,
        poleType: preselectedPoleType
      });
      setRollingSpringheadRecommendation(recommendation);
    } else {
      setRollingSpringheadRecommendation(null);
    }
  }, [formData.bagLength, formData.bagWidth, preselectedPoleType]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setErrors([]); // Clear errors when user makes changes
  };

  const validateCurrentStep = () => {
    if (currentStep === 1) {
      const { bagLength, bagWidth, bagHeight } = formData;
      return bagLength && bagWidth && bagHeight &&
             parseFloat(bagLength) > 0 && parseFloat(bagWidth) > 0 && parseFloat(bagHeight) > 0;
    }
    if (currentStep === 2) {
      return formData.loopType && formData.loopType.trim() !== '';
    }
    if (currentStep === 3) {
      return formData.loopLength &&
             parseFloat(formData.loopLength) >= 15 && parseFloat(formData.loopLength) <= 50;
    }
    return true;
  };

  const saveCalculation = async (calculatedLength) => {
    try {
      const email = localStorage.getItem('indus_user_email');
      if (!email) {
        console.log('No user email found, skipping calculation save');
        return;
      }

      const { error } = await supabase
        .from('pole_calculations')
        .insert({
          user_email: email,
          pole_type: poleType,
          bag_length: parseFloat(formData.bagLength),
          bag_width: parseFloat(formData.bagWidth),
          bag_height: parseFloat(formData.bagHeight),
          loop_type: formData.loopType,
          loop_length: parseFloat(formData.loopLength),
          calculated_pole_length: calculatedLength
        });

      if (error) {
        console.error('Error saving calculation:', error);
      } else {
        console.log('Calculation saved successfully');
      }
    } catch (error) {
      console.error('Unexpected error saving calculation:', error);
    }
  };

  const handleCalculate = () => {
    const validation = validateCalculationInputs({
      ...formData,
      poleType
    });

    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }

    const calculation = calculatePoleLength({
      ...formData,
      poleType
    });

    if (calculation.error) {
      setErrors([calculation.error]);
      setResult(null);
    } else {
      setErrors([]);
      setResult(calculation);

      // Save calculation to database
      saveCalculation(calculation.poleLength);

      // Automatically apply the result instead of showing step 4
      if (calculation.poleLengthNumeric) {
        const mappedSize = mapLengthToSizeRange(calculation.poleLengthNumeric, poleType);
        if (mappedSize && onCalculationComplete) {
          onCalculationComplete({
            calculatedSize: mappedSize,
            calculatedLength: calculation.poleLength,
            calculation: calculation
          });
        }
      }
    }
  };

  const handleUseResult = () => {
    if (result && result.poleLengthNumeric) {
      const mappedSize = mapLengthToSizeRange(result.poleLengthNumeric, poleType);
      if (mappedSize && onCalculationComplete) {
        onCalculationComplete({
          calculatedSize: mappedSize,
          calculatedLength: result.poleLength,
          calculation: result
        });
      }
    }
  };

  const renderStep1 = () => (
    <StepContainer>
      {embedded && (
        <>
          <StepProgress>
            <ProgressDot active={currentStep >= 1} />
            <ProgressDot active={currentStep >= 2} />
            <ProgressDot active={currentStep >= 3} />
            <ProgressDot active={currentStep >= 4} />
          </StepProgress>
          <StepIndicatorText>Step {currentStep}/4 · {getStepTitle(currentStep)}</StepIndicatorText>
          <StepTitleModern>{getStepTitle(currentStep)}</StepTitleModern>
        </>
      )}
      {!embedded && (
        <StepHeader>
          <StepTitle>Bag Dimensions</StepTitle>
        </StepHeader>
      )}

      <InputGrid>
        <InputGroup>
          <Label>Length (cm)</Label>
          <Input
            type="number"
            value={formData.bagLength}
            onChange={(e) => handleInputChange('bagLength', e.target.value)}
            min="1"
          />
        </InputGroup>
        <InputGroup>
          <Label>Width (cm)</Label>
          <Input
            type="number"
            value={formData.bagWidth}
            onChange={(e) => handleInputChange('bagWidth', e.target.value)}
            min="1"
          />
        </InputGroup>
        <InputGroup>
          <Label>Height (cm)</Label>
          <Input
            type="number"
            value={formData.bagHeight}
            onChange={(e) => handleInputChange('bagHeight', e.target.value)}
            min="1"
          />
        </InputGroup>
      </InputGrid>

      {/* Rolling Springhead Recommendation Warning */}
      {rollingSpringheadRecommendation && rollingSpringheadRecommendation.shouldRecommend && (
        <RollingSpringheadWarning
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <WarningHeader>
            <WarningIcon>
              <AlertCircle size={20} />
            </WarningIcon>
            <WarningTitle>
              Recommended pole: <span style={{ color: 'var(--primary-color)', fontWeight: '600' }}>Rolling Springhead</span>
            </WarningTitle>
          </WarningHeader>
          <WarningText>
            We recommend using the Rolling Springhead for big bags smaller than 95 x 95. If you have any questions or a unique situation, feel free to <a href="mailto:l.veldman@indus-bulklogistics.com?subject=Support with big bag size and pole type">contact us</a>.
          </WarningText>
          <WarningDivider />
        </RollingSpringheadWarning>
      )}

      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
        <CalculateButton 
          onClick={() => setCurrentStep(2)}
          disabled={!validateCurrentStep()}
        >
          Next →
        </CalculateButton>
      </div>
    </StepContainer>
  );

  const renderStep2 = () => (
    <StepContainer>
      {embedded && (
        <>
          <StepProgress>
            <ProgressDot active={currentStep >= 1} />
            <ProgressDot active={currentStep >= 2} />
            <ProgressDot active={currentStep >= 3} />
            <ProgressDot active={currentStep >= 4} />
          </StepProgress>
          <StepIndicatorText>Step {currentStep}/4 · {getStepTitle(currentStep)}</StepIndicatorText>
          <StepTitleModern>{getStepTitle(currentStep)}</StepTitleModern>
        </>
      )}
      {!embedded && (
        <StepHeader>
          <StepTitle>Loop Type</StepTitle>
        </StepHeader>
      )}

      <LoopTypeContainer>
        {loopTypes.map(type => (
          <LoopTypeCard
            key={type.id}
            selected={formData.loopType === type.name}
            onClick={() => handleInputChange('loopType', type.name)}
          >
            {type.image && (
              <img
                src={type.image}
                alt={type.name}
                style={{
                  width: '48px',
                  height: '48px',
                  marginBottom: '0.5rem',
                  borderRadius: '4px',
                  objectFit: 'contain'
                }}
              />
            )}
            <LoopTypeLabel>{type.name}</LoopTypeLabel>
            <LoopTypeDesc>{type.description}</LoopTypeDesc>
          </LoopTypeCard>
        ))}
      </LoopTypeContainer>

      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
        <CalculateButton
          onClick={() => setCurrentStep(1)}
          secondary
        >
          ← Back
        </CalculateButton>
        <CalculateButton
          onClick={() => setCurrentStep(3)}
          disabled={!validateCurrentStep()}
        >
          Next →
        </CalculateButton>
      </div>
    </StepContainer>
  );

  const renderStep3 = () => (
    <StepContainer>
      {embedded && (
        <>
          <StepProgress>
            <ProgressDot active={currentStep >= 1} />
            <ProgressDot active={currentStep >= 2} />
            <ProgressDot active={currentStep >= 3} />
            <ProgressDot active={currentStep >= 4} />
          </StepProgress>
          <StepIndicatorText>Step {currentStep}/4 · {getStepTitle(currentStep)}</StepIndicatorText>
          <StepTitleModern>{getStepTitle(currentStep)}</StepTitleModern>
        </>
      )}
      {!embedded && (
        <StepHeader>
          <StepTitle>Loop Length</StepTitle>
        </StepHeader>
      )}

      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1rem' }}>
        <img
          src="https://qpalpdncuownwqknszro.supabase.co/storage/v1/object/public/landing-page/Loop.webp"
          alt="Loop length illustration"
          style={{
            width: '80px',
            height: 'auto',
            borderRadius: '4px',
            objectFit: 'contain'
          }}
        />
      </div>

      <SliderContainer>
        <SliderLabel>15cm</SliderLabel>
        <Slider
          type="range"
          min="15"
          max="50"
          value={formData.loopLength}
          onChange={(e) => handleInputChange('loopLength', e.target.value)}
        />
        <SliderLabel>50cm</SliderLabel>
        <SliderValue>{formData.loopLength}cm</SliderValue>
      </SliderContainer>

      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
        <CalculateButton
          onClick={() => setCurrentStep(2)}
          secondary
        >
          ← Back
        </CalculateButton>
        <CalculateButton
          onClick={handleCalculate}
          disabled={!validateCurrentStep()}
        >
          Calculate
        </CalculateButton>
      </div>
    </StepContainer>
  );

  const renderStep4 = () => (
    <StepContainer>
      {embedded && (
        <>
          <StepProgress>
            <ProgressDot active={currentStep >= 1} />
            <ProgressDot active={currentStep >= 2} />
            <ProgressDot active={currentStep >= 3} />
            <ProgressDot active={currentStep >= 4} />
          </StepProgress>
          <StepIndicatorText>Step {currentStep}/4 · {getStepTitle(currentStep)}</StepIndicatorText>
          <StepTitleModern>{getStepTitle(currentStep)}</StepTitleModern>
        </>
      )}
      {!embedded && (
        <StepHeader>
          <StepTitle>Result</StepTitle>
        </StepHeader>
      )}

      {result && (
        <ResultContainer>
          <ResultTitle>Calculated Pole Length</ResultTitle>
          <ResultValue>{result.poleLength}</ResultValue>
          <RecommendedSizeBadge>
            Recommended Size: {mapLengthToSizeRange(result.poleLengthNumeric, poleType)}
          </RecommendedSizeBadge>
          {result.details && (
            <ResultDetails>
              {result.details.map((detail, index) => (
                <li key={index}>{detail}</li>
              ))}
            </ResultDetails>
          )}
          <UseResultButton onClick={handleUseResult}>
            Use This Size
          </UseResultButton>
        </ResultContainer>
      )}

      <div style={{ marginTop: '0.5rem' }}>
        <CalculateButton
          onClick={() => setCurrentStep(3)}
          secondary
        >
          ← Recalculate
        </CalculateButton>
      </div>
    </StepContainer>
  );

  return (
    <CalculatorContainer
      embedded={embedded}
      initial={{ opacity: 0, scale: 0.95, y: -20 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95, y: -20 }}
      transition={{
        duration: 0.3,
        ease: [0.4, 0.0, 0.2, 1],
        opacity: { duration: 0.25, ease: 'easeOut' },
        scale: { duration: 0.3, ease: [0.4, 0.0, 0.2, 1] },
        y: { duration: 0.3, ease: [0.4, 0.0, 0.2, 1] }
      }}
    >
      {!embedded && (
        <>
          <CalculatorHeader>
            <CalculatorTitle>
              Pole Calculator
            </CalculatorTitle>
            {onClose && <CloseButton onClick={onClose}>✕</CloseButton>}
          </CalculatorHeader>

          <PoleTypeDisplay>
            Selected: <strong>{poleType || 'Not selected'}</strong>
          </PoleTypeDisplay>
        </>
      )}

      <AnimatePresence mode="wait">
        <motion.div
          key={currentStep}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
        >
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
          {currentStep === 4 && renderStep4()}
        </motion.div>
      </AnimatePresence>

      {errors.length > 0 && (
        <ErrorMessage>
          {errors.map((error, index) => (
            <div key={index}>{error}</div>
          ))}
        </ErrorMessage>
      )}
    </CalculatorContainer>
  );
};

export default InlinePoleCalculator;